import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { styled } from '@mui/material/styles';

const GoogleSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '24px',
    backgroundColor:
      theme.palette.mode === 'light'
        ? '#f1f3f4'
        : theme.palette.background.paper,
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'light' ? '#e8eaed' : theme.palette.action.hover,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 2px 5px 1px rgba(0,0,0,0.1)',
    },
    '& fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #dadce0',
    },
  },
}));

interface RetellCallCreditorNameSearchProps {
  onSubmit: (searchTerm: string) => void;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  initialValue?: string;
}

const RetellCallCreditorNameSearch: React.FC<
  RetellCallCreditorNameSearchProps
> = ({
  onSubmit,
  disabled = false,
  placeholder = 'Search',
  isLoading = false,
  initialValue = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(searchTerm.trim());
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClear = () => {
    setSearchTerm('');
    if (onSubmit) {
      onSubmit('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <GoogleSearchField
        fullWidth
        variant="outlined"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                onClick={handleSubmit}
                disabled={disabled || isLoading}
                edge="start"
              >
                <SearchIcon sx={{ color: '#5f6368' }} />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  sx={{ mr: -1 }}
                >
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default RetellCallCreditorNameSearch;
